import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["policyText", "analyzeButton", "analyzeButtonText", "resultContainer", "storeModal", "storeSearch", "storeResults", "rulesModal", "rulesContainer", "editModal", "editForm", "editPackId", "editPackName", "editPackDescription", "editPackActive", "editPackAdvancedVerification", "deleteModal", "deletePackName", "deleteConfirmButton", "saveForm", "packName", "packDescription", "saveButton", "advancedVerification", "outputVerification"]
  static values = { 
    csrfToken: String 
  }

  connect() {
    console.log("✅ Policy controller connected successfully!")
    console.log('Modal target available:', this.hasStoreModalTarget)
    console.log('Policy text target available:', this.hasPolicyTextTarget)
    console.log('Result container target available:', this.hasResultContainerTarget)
    
    // Make the controller available globally for testing
    window.policyController = this
    
    // Find create buttons
    const createButtons = document.querySelectorAll('[data-action*="policy#showStore"]')
    console.log(`Found ${createButtons.length} Create buttons`)
  }

  disconnect() {
    // Clean up ActionCable subscriptions
    if (this.policyCreationSubscription) {
      this.policyCreationSubscription.unsubscribe()
      this.policyCreationSubscription = null
    }
    if (this.policyAnalysisSubscription) {
      this.policyAnalysisSubscription.unsubscribe()
      this.policyAnalysisSubscription = null
    }
    console.log("Policy controller disconnected")
  }
  
  // Test method for debugging
  testModal() {
    console.log('🧪 Test modal called')
    console.log('Modal target available:', this.hasStoreModalTarget)
    if (this.hasStoreModalTarget) {
      this.storeModalTarget.style.display = 'flex'
      this.storeModalTarget.classList.remove('hidden')
      console.log('🧪 Modal should be visible now')
    } else {
      console.log('❌ No modal target found')
    }
  }

  // Analyze policy text with AI (background job)
  async analyzeText(event) {
    event.preventDefault()
    
    const policyText = this.policyTextTarget.value.trim()
    
    if (!policyText) {
      this.showNotification('Please enter policy text to analyze', 'error')
      return
    }

    this.analyzeButtonTarget.disabled = true
    
    // Update button text (handle both modal and page versions)
    if (this.hasAnalyzeButtonTextTarget) {
      this.analyzeButtonTextTarget.textContent = 'Analyzing...'
    } else {
      this.analyzeButtonTarget.innerHTML = 'Analyzing...'
    }

    // Subscribe to analysis updates via WebSocket
    this.subscribeToAnalysisUpdates()

    // Get Advanced Verification setting
    const advancedVerification = this.hasAdvancedVerificationTarget ? this.advancedVerificationTarget.checked : false
    
    // Update notification based on Advanced Verification
    if (advancedVerification) {
      this.showNotification('Starting Advanced AI Analysis (slower but more accurate)...', 'info')
    }

    try {
      const response = await fetch('/dashboard/analyze-policy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({ 
          text: policyText,
          advanced_verification: advancedVerification
        })
      })

      const result = await response.json()

      if (response.ok) {
        if (result.status === 'job_queued') {
          this.showNotification('Analysis started in background. You\'ll be notified when complete.', 'info')
          // Don't restore button state - wait for WebSocket updates
        } else {
          throw new Error('Unexpected response format')
        }
      } else {
        this.showNotification(result.error || 'Failed to start analysis', 'error')
        this.restoreAnalyzeButton()
      }
    } catch (error) {
      console.error('Analysis error:', error)
      this.showNotification('Failed to start analysis. Please try again.', 'error')
      this.restoreAnalyzeButton()
    }
  }

  // Subscribe to policy analysis updates via ActionCable
  subscribeToAnalysisUpdates() {
    if (this.policyAnalysisSubscription) {
      return // Already subscribed
    }

    this.policyAnalysisSubscription = consumer.subscriptions.create('PolicyAnalysisChannel', {
      connected: () => {
        console.log('Connected to PolicyAnalysisChannel')
      },

      disconnected: () => {
        console.log('Disconnected from PolicyAnalysisChannel')
      },

      received: (data) => {
        console.log('Received policy analysis update:', data)
        this.handleAnalysisUpdate(data)
      }
    })
  }

  // Handle real-time policy analysis updates
  handleAnalysisUpdate(data) {
    switch (data.type) {
      case 'analysis_started':
        this.showNotification(data.message, 'info')
        break

      case 'analysis_completed':
        this.showNotification('Analysis completed successfully!', 'success')
        this.displayAnalysisResult(data)
        this.restoreAnalyzeButton()
        break

      case 'analysis_error':
        this.showNotification(data.message, 'error')
        this.restoreAnalyzeButton()
        break

      default:
        console.log('Unknown policy analysis update type:', data.type)
    }
  }

  // Restore analyze button to normal state
  restoreAnalyzeButton() {
    this.analyzeButtonTarget.disabled = false
    
    // Restore button text (handle both modal and page versions)
    if (this.hasAnalyzeButtonTextTarget) {
      this.analyzeButtonTextTarget.textContent = 'Analyze & Generate Rules'
    } else {
      this.analyzeButtonTarget.innerHTML = `
        <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>
        Analyze & Generate
      `
    }
  }

  displayAnalysisResult(result) {
    if (!this.hasResultContainerTarget) return

    const analysis = result.analysis || {}
    const suggestedRules = result.suggested_rules || []
    
    // Get the current Advanced Verification checkbox state (user's current selection)
    const currentAdvancedVerification = this.hasAdvancedVerificationTarget ? this.advancedVerificationTarget.checked : false

    const resultHtml = `
      <div class="analysis-result bg-slate-700/50 rounded-lg p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-white">Analysis Complete</h3>
          <span class="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium">
            Ready for Review
          </span>
        </div>
        
        <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div class="flex items-start space-x-3">
            <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <div class="text-sm font-medium text-blue-400">Review Generated Rules</div>
              <div class="text-xs text-blue-300 mt-1">
                Please review the AI-generated rules below. You can create the policy pack if you're satisfied with the results.
                ${currentAdvancedVerification ? '<br><strong>Advanced Verification:</strong> Enabled (AI-powered contextual analysis)' : ''}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Summary Stats -->
        <div class="grid grid-cols-3 gap-4 mb-6">
          <div class="text-center p-3 bg-slate-600/50 rounded-lg">
            <div class="text-xl font-bold text-white">${analysis.estimated_rules_count || 0}</div>
            <div class="text-xs text-slate-400">Rules Generated</div>
          </div>
          <div class="text-center p-3 bg-slate-600/50 rounded-lg">
            <div class="text-xl font-bold text-white">${analysis.confidence || 0}%</div>
            <div class="text-xs text-slate-400">Confidence</div>
          </div>
          <div class="text-center p-3 bg-slate-600/50 rounded-lg">
            <div class="text-xl font-bold text-white">${analysis.data_types_mentioned?.length || 0}</div>
            <div class="text-xs text-slate-400">Data Types</div>
          </div>
        </div>
        
        <!-- Summary -->
        ${analysis.summary ? `
          <div class="mb-6">
            <h4 class="font-medium text-white mb-2">Summary</h4>
            <p class="text-sm text-slate-300">${analysis.summary}</p>
          </div>
        ` : ''}
        
        <!-- Generated Rules -->
        ${suggestedRules.length > 0 ? `
          <div class="mb-6">
            <h4 class="font-medium text-white mb-3">Generated Policy Rules</h4>
            <div class="space-y-3">
              ${suggestedRules.map(rule => `
                <div class="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="font-medium text-white">${rule.name}</div>
                      <div class="text-sm text-slate-300 mt-1">${rule.description}</div>
                      <div class="text-xs text-slate-400 mt-2">
                        Type: ${rule.rule_type} | Pattern: ${rule.pattern}
                      </div>
                    </div>
                    <span class="px-2 py-1 text-xs font-medium rounded ${rule.severity === 'critical' ? 'bg-red-500/20 text-red-300' : rule.severity === 'high' ? 'bg-orange-500/20 text-orange-300' : 'bg-yellow-500/20 text-yellow-300'}">
                      ${rule.severity.toUpperCase()}
                    </span>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}
        
        <!-- Data Types -->
        ${analysis.data_types_mentioned?.length > 0 ? `
          <div class="mb-6">
            <h4 class="font-medium text-white mb-2">Detected Data Types</h4>
            <div class="flex flex-wrap gap-2">
              ${analysis.data_types_mentioned.map(type => `
                <span class="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">${type}</span>
              `).join('')}
            </div>
          </div>
        ` : ''}
        
        <div class="flex gap-3">
          <button type="button" 
                  id="create-pack-btn"
                  class="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  data-action="click->policy#createPolicyPack">
            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Create Policy Pack</span>
          </button>
          <button type="button" 
                  class="bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors"
                  data-action="click->policy#downloadRules">
            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Download Rules
          </button>
          <button type="button" 
                  class="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  data-action="click->policy#editAnalysis">
            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            Edit & Re-analyze
          </button>
        </div>
        
        <!-- Progress indicator (initially hidden) -->
        <div id="creation-progress" class="mt-4 hidden">
          <div class="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div class="flex items-center space-x-3">
              <svg class="animate-spin w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div>
                <div class="text-sm font-medium text-blue-400">Creating Policy Pack</div>
                <div class="text-xs text-blue-300">This may take 60-90 seconds due to AI processing...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.resultContainerTarget.innerHTML = resultHtml
    
    // Auto-generate a suggested name based on policy content for new page
    if (this.hasPackNameTarget) {
      const policyText = this.policyTextTarget.value
      const suggestedName = this.generatePackName(policyText)
      this.packNameTarget.value = suggestedName
    }
  }

  // Upload policy text file
  async uploadPolicyFile(event) {
    const file = event.target.files[0]
    if (!file) return

    if (file.type !== 'text/plain' && !file.name.endsWith('.txt')) {
      this.showNotification('Please select a text file (.txt)', 'error')
      return
    }

    try {
      const text = await this.readFileAsText(file)
      this.policyTextTarget.value = text
      this.showNotification('File uploaded successfully', 'success')
    } catch (error) {
      console.error('File upload error:', error)
      this.showNotification('Failed to read file', 'error')
    }
  }

  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsText(file)
    })
  }

  // Show policy creation modal
  showStore(event) {
    if (event) event.preventDefault()
    
    console.log('🚀 Opening Create Policy modal')
    
    if (this.hasStoreModalTarget) {
      // Show modal using both class removal and inline style
      this.storeModalTarget.classList.remove('hidden')
      this.storeModalTarget.style.display = 'flex'
      
      console.log('✅ Modal opened successfully')
      
      // Reset form
      if (this.hasPolicyTextTarget) {
        this.policyTextTarget.value = ''
      }
      if (this.hasResultContainerTarget) {
        this.resultContainerTarget.innerHTML = `
          <div class="text-center text-slate-400 mt-12">
            <svg class="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            <p>Generated policy rules will appear here</p>
            <p class="text-sm mt-1">Enter policy description and click "Analyze & Generate"</p>
          </div>
        `
      }
    } else {
      console.error('Store modal target not found')
      console.log('Available targets:', this.targets)
    }
  }

  hideStore(event) {
    if (event) event.preventDefault()
    
    console.log('❌ Closing Create Policy modal')
    if (this.hasStoreModalTarget) {
      this.storeModalTarget.classList.add('hidden')
      this.storeModalTarget.style.display = 'none'
    }
  }

  displayStoreItems(items) {
    if (!this.hasStoreResultsTarget) return

    const itemsHtml = items.map(item => `
      <div class="store-item bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer"
           data-action="click->policy#selectStoreItem" 
           data-policy-item-id="${item.id}">
        <h3 class="font-semibold text-gray-900">${item.name}</h3>
        <p class="text-sm text-gray-600 mt-1">${item.description}</p>
        <div class="flex justify-between items-center mt-3">
          <span class="text-xs text-gray-500">${item.rules_count} rules</span>
          <span class="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
            ${item.category}
          </span>
        </div>
      </div>
    `).join('')

    this.storeResultsTarget.innerHTML = itemsHtml
  }

  // Filter store items
  filterStore(event) {
    const searchTerm = event.target.value.toLowerCase()
    const items = this.storeResultsTarget.querySelectorAll('.store-item')
    
    items.forEach(item => {
      const title = item.querySelector('h3').textContent.toLowerCase()
      const description = item.querySelector('p').textContent.toLowerCase()
      
      if (title.includes(searchTerm) || description.includes(searchTerm)) {
        item.style.display = 'block'
      } else {
        item.style.display = 'none'
      }
    })
  }

  // Select store item
  async selectStoreItem(event) {
    const itemId = event.currentTarget.dataset.policyItemId
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/store/${itemId}`)
      const item = await response.json()
      
      // Import the selected policy pack
      await this.importPolicyPack(item)
      this.hideStore(event)
      
    } catch (error) {
      console.error('Store item selection error:', error)
      this.showNotification('Failed to import policy pack', 'error')
    }
  }

  async importPolicyPack(policyPack) {
    try {
      const response = await fetch('/api/dashboard/policy_packs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({ policy_pack: policyPack })
      })

      if (response.ok) {
        this.showNotification('Policy pack imported successfully', 'success')
        // Refresh the page or update the UI
        window.location.reload()
      } else {
        const error = await response.json()
        this.showNotification(error.message || 'Import failed', 'error')
      }
    } catch (error) {
      console.error('Import error:', error)
      this.showNotification('Import failed', 'error')
    }
  }

  // Create policy pack directly (for new policy pack page)
  async createPolicyPackDirect(event) {
    event.preventDefault()
    
    const policyText = this.policyTextTarget.value.trim()
    if (!policyText) {
      this.showNotification('Please enter policy text to analyze', 'error')
      return
    }

    // Get pack name and description from form
    const packName = this.hasPackNameTarget ? this.packNameTarget.value.trim() : ''
    const packDescription = this.hasPackDescriptionTarget ? this.packDescriptionTarget.value.trim() : ''
    
    if (!packName) {
      // Auto-generate name if not provided
      const generatedName = this.generatePackName(policyText)
      if (this.hasPackNameTarget) {
        this.packNameTarget.value = generatedName
      }
    }

    const finalPackName = packName || this.generatePackName(policyText)
    const finalDescription = packDescription || "Auto-generated from policy analysis"
    const advancedVerification = this.hasAdvancedVerificationTarget ? this.advancedVerificationTarget.checked : false
    const outputVerification = this.hasOutputVerificationTarget ? this.outputVerificationTarget.checked : false

    // Show loading states and notify user
    this.showCreationLoading(true) // Always use new page version
    
    const notificationMessage = advancedVerification 
      ? 'Starting background AI analysis with Advanced Verification (slower but more accurate)...'
      : 'Starting background AI analysis and policy pack creation...'
    this.showNotification(notificationMessage, 'info')

    try {
      // Subscribe to policy creation updates via WebSocket
      this.subscribeToCreationUpdates()
      
      const response = await fetch('/api/dashboard/policy_packs/create_from_analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({
          policy_text: policyText,
          name: finalPackName,
          description: finalDescription,
          advanced_verification: advancedVerification,
          output_verification: outputVerification
        })
      })

      if (response.ok) {
        const result = await response.json()
        if (result.status === 'job_queued') {
          this.showNotification(`Policy pack "${finalPackName}" queued for creation. Processing in background...`, 'success')
          // Update the UI to show progress
          this.showCreationProgress(finalPackName)
        } else {
          throw new Error('Unexpected response format')
        }
      } else {
        const error = await response.json()
        this.showNotification(error.error || 'Failed to start creation', 'error')
        this.hideCreationLoading(true)
      }
    } catch (error) {
      console.error('Policy pack creation error:', error)
      this.showNotification('Failed to start creation', 'error')
      this.hideCreationLoading(true)
    }
  }

  // Show creation progress in the results area
  showCreationProgress(packName) {
    if (!this.hasResultContainerTarget) return

    const progressHtml = `
      <div class="creation-progress text-center py-16">
        <div class="flex items-center justify-center mb-4">
          <svg class="animate-spin w-12 h-12 text-blue-400" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-white mb-2">Creating "${packName}"</h3>
        <p class="text-slate-400 mb-2">AI is analyzing your policy and generating security rules...</p>
        <p class="text-sm text-slate-500">This process runs in the background and typically takes 30-60 seconds</p>
        
        <div class="mt-6 bg-slate-800/50 rounded-lg p-4">
          <div class="flex items-center justify-between text-sm">
            <span class="text-slate-300">Progress</span>
            <span class="text-blue-400">Processing...</span>
          </div>
          <div class="mt-2 bg-slate-700 rounded-full h-2">
            <div class="bg-blue-500 h-2 rounded-full animate-pulse" style="width: 45%"></div>
          </div>
        </div>
      </div>
    `

    this.resultContainerTarget.innerHTML = progressHtml
  }

  // Create policy pack from analysis (for analysis results)
  async createPolicyPack(event) {
    event.preventDefault()
    
    // Get the analysis results from the DOM
    const analysisResult = this.resultContainerTarget.querySelector('.analysis-result')
    if (!analysisResult) {
      this.showNotification('No analysis results to create policy pack from', 'error')
      return
    }

    // Determine if we're on the new page or in analysis results
    const isNewPage = this.hasPackNameTarget
    
    // Get pack name and description from form or generate defaults
    let packName, packDescription
    
    if (isNewPage) {
      packName = this.packNameTarget.value.trim()
      packDescription = this.packDescriptionTarget.value.trim()
      
      if (!packName) {
        this.showNotification('Please enter a policy pack name', 'error')
        return
      }
    } else {
      // Generate automatic name for analysis results button
      const policyText = this.policyTextTarget.value
      packName = this.generatePackName(policyText)
      packDescription = "Auto-generated from policy analysis"
    }

    // Use the current Advanced Verification checkbox state (not the stored analysis result)
    // This ensures the user's current selection is respected
    const advancedVerification = this.hasAdvancedVerificationTarget ? this.advancedVerificationTarget.checked : false
    const outputVerification = this.hasOutputVerificationTarget ? this.outputVerificationTarget.checked : false

    // Show loading states and notify user
    this.showCreationLoading(isNewPage)
    this.showNotification('Starting background AI analysis... You\'ll be notified when complete.', 'info')

    try {
      // Subscribe to policy creation updates via WebSocket
      this.subscribeToCreationUpdates()
      
      const response = await fetch('/api/dashboard/policy_packs/create_from_analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify({
          policy_text: this.policyTextTarget.value,
          name: packName,
          description: packDescription,
          advanced_verification: advancedVerification,
          output_verification: outputVerification
        })
      })

      if (response.ok) {
        const result = await response.json()
        if (result.status === 'job_queued') {
          this.showNotification(`Policy pack "${packName}" queued for creation. Processing in background...`, 'success')
          // Don't hide loading states - wait for WebSocket updates
        } else {
          throw new Error('Unexpected response format')
        }
      } else {
        const error = await response.json()
        this.showNotification(error.error || 'Failed to start creation', 'error')
        this.hideCreationLoading(isNewPage)
      }
    } catch (error) {
      console.error('Policy pack creation error:', error)
      this.showNotification('Failed to start creation', 'error')
      this.hideCreationLoading(isNewPage)
    }
  }

  // Subscribe to policy creation updates via ActionCable
  subscribeToCreationUpdates() {
    if (this.policyCreationSubscription) {
      return // Already subscribed
    }

    this.policyCreationSubscription = consumer.subscriptions.create('PolicyCreationChannel', {
      connected: () => {
        console.log('Connected to PolicyCreationChannel')
      },

      disconnected: () => {
        console.log('Disconnected from PolicyCreationChannel')
      },

      received: (data) => {
        console.log('Received policy creation update:', data)
        this.handleCreationUpdate(data)
      }
    })
  }

  // Handle real-time policy creation updates
  handleCreationUpdate(data) {
    const isNewPage = this.hasPackNameTarget

    switch (data.type) {
      case 'policy_creation_started':
        this.showNotification(data.message, 'info')
        break

      case 'policy_creation_completed':
        this.showNotification(data.message, 'success')
        this.hideCreationLoading(isNewPage)
        
        // Redirect to policy packs page after success
        setTimeout(() => {
          window.location.href = data.redirect_url || '/dashboard/policy-packs'
        }, 2000)
        break

      case 'policy_creation_error':
        this.showNotification(data.message, 'error')
        this.hideCreationLoading(isNewPage)
        break

      default:
        console.log('Unknown policy creation update type:', data.type)
    }
  }

  // Generate a suggested pack name based on policy content
  generatePackName(policyText) {
    const text = policyText.toLowerCase()
    
    // Common policy types and their suggested names
    const patterns = [
      { keywords: ['medical', 'health', 'patient', 'hipaa'], name: 'Medical Privacy Policy' },
      { keywords: ['financial', 'credit', 'bank', 'payment', 'ssn'], name: 'Financial Data Protection' },
      { keywords: ['pii', 'personal', 'privacy', 'data'], name: 'Personal Data Privacy' },
      { keywords: ['legal', 'attorney', 'confidential', 'privileged'], name: 'Legal Confidentiality' },
      { keywords: ['employee', 'hr', 'personnel', 'workplace'], name: 'Employee Privacy Policy' },
      { keywords: ['customer', 'client', 'user'], name: 'Customer Data Protection' }
    ]
    
    // Find the best matching pattern
    for (const pattern of patterns) {
      if (pattern.keywords.some(keyword => text.includes(keyword))) {
        return pattern.name
      }
    }
    
    // Default fallback
    return `Policy Pack ${new Date().toLocaleDateString()}`
  }

  // Show loading states during policy pack creation
  showCreationLoading(isNewPage) {
    if (isNewPage) {
      // Handle save form button
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = true
        this.saveButtonTarget.textContent = 'Creating...'
      }
    } else {
      // Handle analysis results button
      const createBtn = document.getElementById('create-pack-btn')
      const progressDiv = document.getElementById('creation-progress')
      
      if (createBtn) {
        createBtn.disabled = true
        const span = createBtn.querySelector('span')
        if (span) span.textContent = 'Creating...'
      }
      
      if (progressDiv) {
        progressDiv.classList.remove('hidden')
      }
    }
  }

  // Hide loading states after policy pack creation
  hideCreationLoading(isNewPage) {
    if (isNewPage) {
      // Handle save form button
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false
        this.saveButtonTarget.textContent = 'Create Policy Pack'
      }
    } else {
      // Handle analysis results button
      const createBtn = document.getElementById('create-pack-btn')
      const progressDiv = document.getElementById('creation-progress')
      
      if (createBtn) {
        createBtn.disabled = false
        const span = createBtn.querySelector('span')
        if (span) span.textContent = 'Create Policy Pack'
      }
      
      if (progressDiv) {
        progressDiv.classList.add('hidden')
      }
    }
  }

  // Download rules
  downloadRules(event) {
    event.preventDefault()
    
    const analysisResult = this.resultContainerTarget.querySelector('.analysis-result')
    if (!analysisResult) {
      this.showNotification('No analysis results to download', 'error')
      return
    }

    // Create downloadable content
    const content = this.policyTextTarget.value
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = `policy-rules-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    this.showNotification('Rules downloaded successfully', 'success')
  }

  // Edit analysis - clear results and allow re-editing
  editAnalysis(event) {
    event.preventDefault()
    
    // Clear the results container to allow user to edit and re-analyze
    if (this.hasResultContainerTarget) {
      this.resultContainerTarget.innerHTML = `
        <div class="text-center py-12 text-slate-400">
          <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
          </svg>
          <p class="text-sm">Edit your policy description above and click "Analyze & Generate Rules" to try again.</p>
        </div>
      `
    }
    
    // Re-enable the analyze button
    if (this.hasAnalyzeButtonTarget) {
      this.analyzeButtonTarget.disabled = false
      if (this.hasAnalyzeButtonTextTarget) {
        this.analyzeButtonTextTarget.textContent = 'Analyze & Generate Rules'
      }
    }
    
    // Focus back on the policy text area
    if (this.hasPolicyTextTarget) {
      this.policyTextTarget.focus()
    }
    
    this.showNotification('You can now edit your policy description and re-analyze', 'info')
  }

  showNotification(message, type = 'info') {
    // Try to show notification in the notification container if it exists
    const container = document.getElementById('notification-container')
    
    if (container) {
      this.displayNotification(container, message, type)
    } else {
      // Fallback: dispatch a custom event that the dashboard controller can handle
      this.dispatch('notification', { 
        detail: { message, type } 
      })
    }
  }

  displayNotification(container, message, type) {
    const notification = document.createElement('div')
    notification.className = `notification-${type} max-w-sm mx-auto bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 transform translate-x-full`
    
    const bgColor = {
      success: 'bg-green-50 border-green-200',
      error: 'bg-red-50 border-red-200',
      warning: 'bg-yellow-50 border-yellow-200',
      info: 'bg-blue-50 border-blue-200'
    }[type] || 'bg-gray-50 border-gray-200'
    
    const iconColor = {
      success: 'text-green-500',
      error: 'text-red-500',
      warning: 'text-yellow-500',
      info: 'text-blue-500'
    }[type] || 'text-gray-500'
    
    const icon = {
      success: 'M5 13l4 4L19 7',
      error: 'M6 18L18 6M6 6l12 12',
      warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z',
      info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    }[type] || 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    
    notification.innerHTML = `
      <div class="p-4 border ${bgColor}">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 ${iconColor}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${icon}"/>
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-900">${message}</p>
          </div>
          <div class="ml-auto pl-3">
            <button class="inline-flex text-gray-400 hover:text-gray-500" onclick="this.closest('.notification-${type}').remove()">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `
    
    container.appendChild(notification)
    
    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full')
    }, 100)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full')
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove()
        }
      }, 300)
    }, 5000)
  }

  // Show rules for a policy pack
  async showRules(event) {
    event.preventDefault()
    const packId = event.currentTarget.dataset.policyPackId
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/${packId}`, {
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.displayRules(data.policy_pack)
        this.rulesModalTarget.classList.remove('hidden')
        this.rulesModalTarget.style.display = 'flex'
      } else {
        this.showNotification('Failed to load rules', 'error')
      }
    } catch (error) {
      console.error('Error loading rules:', error)
      this.showNotification('Error loading rules', 'error')
    }
  }

  // Hide rules modal
  hideRules() {
    this.rulesModalTarget.classList.add('hidden')
    this.rulesModalTarget.style.display = 'none'
  }

  // Display rules in the modal
  displayRules(policyPack) {
    const rules = policyPack.rules || []
    
    // Store the current pack ID for refreshing after rule changes
    this.rulesContainerTarget.dataset.policyPackId = policyPack.id
    
    const rulesHtml = `
      <div class="mb-4">
        <h3 class="text-xl font-semibold text-white">${policyPack.name}</h3>
        <p class="text-slate-400">${policyPack.description || 'No description'}</p>
        <p class="text-sm text-slate-500 mt-2">${rules.length} rules • Version ${policyPack.version}</p>
      </div>
      
      ${rules.length > 0 ? rules.map(rule => `
        <div class="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-2">
                <h4 class="font-medium text-white">${rule.name}</h4>
                <span class="px-2 py-1 text-xs font-medium rounded ${
                  rule.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 
                  rule.severity === 'high' ? 'bg-orange-500/20 text-orange-300' : 
                  rule.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-blue-500/20 text-blue-300'
                }">
                  ${rule.severity.toUpperCase()}
                </span>
                <span class="px-2 py-1 text-xs font-medium rounded bg-slate-500/20 text-slate-300">
                  ${rule.rule_type}
                </span>
                <span class="px-2 py-1 text-xs font-medium rounded ${
                  rule.enabled ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-400'
                }">
                  ${rule.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p class="text-sm text-slate-300 mb-2">${rule.description}</p>
              <div class="text-xs text-slate-400">
                <strong>Pattern:</strong> <code class="bg-slate-800 px-1 rounded">${rule.pattern}</code>
              </div>
            </div>
            <div class="flex space-x-1 ml-4">
              <button onclick="window.policyController.toggleRule(${rule.id})" 
                      class="p-1 text-slate-400 hover:text-blue-400 rounded" 
                      title="${rule.enabled ? 'Disable' : 'Enable'}">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  ${rule.enabled ? 
                    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>' :
                    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5v14l11-7z"></path>'
                  }
                </svg>
              </button>
              
              <button onclick="window.policyController.deleteRule(${rule.id}, '${rule.name.replace(/'/g, '\\\'')}')" 
                      class="p-1 text-slate-400 hover:text-red-400 rounded" 
                      title="Delete Rule">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `).join('') : '<p class="text-slate-400 text-center py-8">No rules in this policy pack</p>'}
    `
    
    this.rulesContainerTarget.innerHTML = rulesHtml
  }

  // Show edit modal for policy pack
  async editPack(event) {
    event.preventDefault()
    const packId = event.currentTarget.dataset.policyPackId
    
    // Check if required targets exist
    if (!this.hasEditModalTarget) {
      console.error('Edit modal target not found')
      this.showNotification('Edit modal not available', 'error')
      return
    }
    
    if (!this.hasEditPackIdTarget) {
      console.error('Edit form targets not found')
      this.showNotification('Edit form not available', 'error')
      return
    }
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/${packId}`, {
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        const pack = data.policy_pack
        
        // Populate form
        this.editPackIdTarget.value = pack.id
        this.editPackNameTarget.value = pack.name
        this.editPackDescriptionTarget.value = pack.description || ''
        this.editPackActiveTarget.checked = pack.active
        this.editPackAdvancedVerificationTarget.checked = pack.advanced_verification || false
        
        // Show modal
        this.editModalTarget.classList.remove('hidden')
        this.editModalTarget.style.display = 'flex'
      } else {
        this.showNotification('Failed to load policy pack', 'error')
      }
    } catch (error) {
      console.error('Error loading policy pack:', error)
      this.showNotification('Error loading policy pack', 'error')
    }
  }

  // Hide edit modal
  hideEdit() {
    this.editModalTarget.classList.add('hidden')
    this.editModalTarget.style.display = 'none'
  }

  // Save edit changes
  async saveEdit(event) {
    event.preventDefault()
    
    const packId = this.editPackIdTarget.value
    const formData = {
      name: this.editPackNameTarget.value,
      description: this.editPackDescriptionTarget.value,
      active: this.editPackActiveTarget.checked,
      advanced_verification: this.editPackAdvancedVerificationTarget.checked
    }
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/${packId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        this.showNotification('Policy pack updated successfully', 'success')
        this.hideEdit()
        window.location.reload() // Refresh to show changes
      } else {
        const error = await response.json()
        this.showNotification(error.message || 'Update failed', 'error')
      }
    } catch (error) {
      console.error('Error updating policy pack:', error)
      this.showNotification('Update failed', 'error')
    }
  }

  // Show delete confirmation
  deletePack(event) {
    event.preventDefault()
    const packId = event.currentTarget.dataset.policyPackId
    const packName = event.currentTarget.dataset.policyPackName
    
    this.deletePackNameTarget.textContent = packName
    this.deleteConfirmButtonTarget.dataset.policyPackId = packId
    
    this.deleteModalTarget.classList.remove('hidden')
    this.deleteModalTarget.style.display = 'flex'
  }

  // Hide delete modal
  hideDelete() {
    this.deleteModalTarget.classList.add('hidden')
    this.deleteModalTarget.style.display = 'none'
  }

  // Confirm delete
  async confirmDelete(event) {
    event.preventDefault()
    const packId = event.currentTarget.dataset.policyPackId
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/${packId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        this.showNotification('Policy pack deleted successfully', 'success')
        this.hideDelete()
        window.location.reload() // Refresh to show changes
      } else {
        const error = await response.json()
        this.showNotification(error.message || 'Delete failed', 'error')
      }
    } catch (error) {
      console.error('Error deleting policy pack:', error)
      this.showNotification('Delete failed', 'error')
    }
  }

  // Toggle policy pack active/inactive
  async togglePack(event) {
    event.preventDefault()
    const packId = event.currentTarget.dataset.policyPackId
    
    try {
      const response = await fetch(`/api/dashboard/policy_packs/${packId}/toggle`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.showNotification(`Policy pack ${data.active ? 'activated' : 'deactivated'}`, 'success')
        window.location.reload() // Refresh to show changes
      } else {
        this.showNotification('Toggle failed', 'error')
      }
    } catch (error) {
      console.error('Error toggling policy pack:', error)
      this.showNotification('Toggle failed', 'error')
    }
  }

  // Toggle individual rule
  async toggleRule(ruleId, ruleName = null) {
    try {
      const response = await fetch(`/api/dashboard/policy_rules/${ruleId}/toggle`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        const ruleDisplayName = ruleName || 'Rule'
        this.showNotification(`${ruleDisplayName} ${data.enabled ? 'enabled' : 'disabled'}`, 'success')
        
        // Refresh the rules display
        const packId = this.rulesContainerTarget.dataset.policyPackId
        if (packId) {
          const showEvent = { currentTarget: { dataset: { policyPackId: packId } }, preventDefault: () => {} }
          this.showRules(showEvent)
        }
      } else {
        this.showNotification('Toggle failed', 'error')
      }
    } catch (error) {
      console.error('Error toggling rule:', error)
      this.showNotification('Toggle failed', 'error')
    }
  }

  // Delete individual rule with confirmation
  async deleteRule(ruleId, ruleName) {
    // Show confirmation dialog
    const confirmed = confirm(`Are you sure you want to delete the rule "${ruleName}"?\n\nThis action cannot be undone.`)
    
    if (!confirmed) {
      return
    }

    try {
      const response = await fetch(`/api/dashboard/policy_rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        this.showNotification(`Rule "${ruleName}" deleted successfully`, 'success')
        
        // Refresh the rules display immediately to show the updated list
        const packId = this.rulesContainerTarget.dataset.policyPackId
        if (packId) {
          // Refresh the rules modal by re-fetching the policy pack data
          const showEvent = { 
            currentTarget: { dataset: { policyPackId: packId } }, 
            preventDefault: () => {} 
          }
          this.showRules(showEvent)
        }
      } else {
        const error = await response.json()
        this.showNotification(error.message || 'Delete failed', 'error')
      }
    } catch (error) {
      console.error('Error deleting rule:', error)
      this.showNotification('Delete failed', 'error')
    }
  }

  // Duplicate policy pack
  async duplicatePack(packId) {
    try {
      this.showNotification('Duplicating policy pack...', 'info')
      
      const response = await fetch(`/api/dashboard/policy_packs/${packId}/duplicate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.showNotification(data.message, 'success')
        
        // Redirect to policy packs list after short delay to show notification
        setTimeout(() => {
          window.location.href = '/dashboard/policy-packs'
        }, 1500)
      } else {
        const error = await response.json()
        this.showNotification(error.message || 'Duplication failed', 'error')
      }
    } catch (error) {
      console.error('Error duplicating policy pack:', error)
      this.showNotification('Duplication failed', 'error')
    }
  }
} 